import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const BudgetRequestForm = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error(t("toast.mustBeLoggedIn"));
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error(t("toast.validAmount"));
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("budget_purchase_requests")
        .insert({
          user_id: user.id,
          amount: amountNum,
          status: "pending"
        });

      if (error) throw error;

      toast.success(t("toast.requestSubmitted"));
      setAmount("");
    } catch (error: any) {
      console.error("Error submitting request:", error);
      toast.error(t("toast.requestFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("budget.requestTitle")}</CardTitle>
        <CardDescription>
          {t("budget.requestDesc")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="amount">{t("budget.addAmount")} ($)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="10.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("budget.submitRequest")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
